import React, { useMemo, useState } from 'react'
import {
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	Menu,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	IconButton,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../../CustomDataGrid'
import { useModal } from '../../../../../store/ModalProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import type {
	DriverServiceTotalsModalData,
	VehicleServiceTotalsByTypeRow,
	VehicleServiceTotalsModalData,
	VehicleServiceTotalsRow,
} from '../../../../../types/modal'
import { useNavigate } from 'react-router-dom'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const readonlyFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#F5F7FC',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
		WebkitTextFillColor: '#092056',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .row--alert': {
		backgroundColor: 'rgba(255, 232, 216, 0.6)',
	},
	'& .row--warning': {
		backgroundColor: 'rgba(255, 243, 226, 0.7)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const servicesTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '0 16px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 16px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.04)',
	},
	'& .serviceRowActions': {
		opacity: 0,
		pointerEvents: 'none',
		transition: 'opacity 0.15s ease',
	},
	'& .MuiDataGrid-row:hover .serviceRowActions': {
		opacity: 1,
		pointerEvents: 'auto',
	},
}

const totalsTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '8px 16px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 16px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiDataGrid-cellContent': {
		lineHeight: 1.3,
		fontWeight: 400,
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'transparent',
	},
	'& .row--totals': {
		backgroundColor: 'rgba(245, 247, 252, 0.5)',
	},
	'& .row--totals:hover': {
		backgroundColor: 'rgba(245, 247, 252, 0.5)',
	},
}

const menuPaperSx = {
	borderRadius: '4px',
	boxShadow:
		'0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
}

const checkboxSx = {
	color: '#8695AF',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
}

const checkboxLabelSx = {
	fontFamily: 'Poppins, sans-serif',
	fontSize: '12px',
	fontWeight: 500,
	color: '#092056',
}

const defaultServices: VehicleServiceTotalsRow[] = [
	{
		id: '1',
		requisicao: '95509',
		motorista: 'Alexandre Gomes',
		numMec: '0137',
		dataInicio: '02/05/2024',
		horaInicio: '09:00',
		localInicio: 'Lisboa',
		localTermino: 'Torres Vedras',
		horaTermino: '10:00',
		dataTermino: '02/05/2024',
		cliente: 'Best Transfers 4 U',
	},
	{
		id: '2',
		requisicao: '84211',
		motorista: 'Beatriz Silva',
		numMec: '0298',
		dataInicio: '03/05/2024',
		horaInicio: '14:00',
		localInicio: 'Lisboa',
		localTermino: 'Almada',
		horaTermino: '15:00',
		dataTermino: '03/05/2024',
		cliente: 'Scenic Tours',
	},
	{
		id: '3',
		requisicao: '84212',
		motorista: 'Carlos Cardoso',
		numMec: '0123',
		dataInicio: '03/05/2024',
		horaInicio: '09:00',
		localInicio: 'Braga',
		localTermino: 'Guimarães',
		horaTermino: '10:30',
		dataTermino: '03/05/2024',
		cliente: 'Cultural Walks',
	},
	{
		id: '4',
		requisicao: '84213',
		motorista: 'Ana Almeida',
		numMec: '0211',
		dataInicio: '04/05/2024',
		horaInicio: '16:00',
		localInicio: 'Coimbra',
		localTermino: 'Figueira da Foz',
		horaTermino: '17:30',
		dataTermino: '04/05/2024',
		cliente: 'Sunset Escapes',
	},
]

const defaultTotals: VehicleServiceTotalsByTypeRow[] = [
	{
		id: '1',
		tipo: 'Agências Viagens',
		numero: '5',
		valor: '405,90',
		kmsCarga: '750',
		kmsVazio: '50',
		totalKms: '800',
	},
	{
		id: '2',
		tipo: 'Ocasional',
		numero: '10',
		valor: '525,00',
		kmsCarga: '1140',
		kmsVazio: '196',
		totalKms: '1336',
	},
	{
		id: '3',
		tipo: 'Totais',
		numero: '15',
		valor: '930,90',
		kmsCarga: '1890',
		kmsVazio: '246',
		totalKms: '2136',
	},
]

const defaultStats = {
	kmsCarga: '1890',
	kmsVazio: '246',
	totalKms: '2136',
	duracao: '22:30',
}

const CustomModalVehicleServiceTotals: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers, openModal } = useModal()
	const navigate = useNavigate()
	const data = modalData as VehicleServiceTotalsModalData | undefined
	const [handlePrint, handleSend, handleExport] = modalHandlers ?? []

	const [startDate, setStartDate] = useState<Dayjs | null>(
		data?.startDate ? dayjs(data.startDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(
		data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY') : dayjs('08/05/2024', 'DD/MM/YYYY'),
	)
	const [filial, setFilial] = useState(data?.filial ?? 'Transol')
	const [vehicle, setVehicle] = useState(data?.vehicle ?? '0046')
	const [includeScheduleTime, setIncludeScheduleTime] = useState(data?.includeScheduleTime ?? true)
	const [servicesDensity, setServicesDensity] = useState<DensityMode>('standard')
	const [totalsDensity, setTotalsDensity] = useState<DensityMode>('standard')
	const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(null)
	const [rowMenuId, setRowMenuId] = useState<string | number | null>(null)

	// Use our custom animation hook
	const { animateRows } = useDataGridAnimations(isOpen)

	const services = data?.services?.length ? data.services : defaultServices
	const totals = data?.totals?.length ? data.totals : defaultTotals
	const stats = { ...defaultStats, ...(data?.stats ?? {}) }
	const footerVariant = data?.footerVariant ?? 'send'

	const handleOpenRowMenu = (event: React.MouseEvent<HTMLButtonElement>, rowId: string | number) => {
		setRowMenuAnchorEl(event.currentTarget)
		setRowMenuId(rowId)
	}

	const handleCloseRowMenu = () => {
		setRowMenuAnchorEl(null)
		setRowMenuId(null)
	}

	const handleRowMenuAction = (action: string) => {
		const selectedRow = services.find(row => row.id === rowMenuId)
		if (action === 'Ficha do Motorista') {
			const driverId = selectedRow?.numMec ?? rowMenuId ?? 1
			handleCloseRowMenu()
			closeModal()
			navigate(`/schedules/driver/${driverId}`)
			return
		}

		if (action === 'Serviços e Totais do Motorista') {
			const modalData: DriverServiceTotalsModalData = {
				driverId: selectedRow?.numMec ?? 1,
				startDate: '02/05/2024',
				endDate: '08/05/2024',
				filial: 'Transol',
				driver: selectedRow?.motorista ?? 'Motorista',
				includeScheduleTime: true,
				services: [],
				totals: [],
				stats: {
					kmsCarga: '1890',
					kmsVazio: '246',
					totalValue: '930,90',
				},
			}
			handleCloseRowMenu()
			openModal('Servicos e Totais por Motorista', modalData, [])
			return
		}

		console.log('Serviços por viatura menu:', action, rowMenuId)
		handleCloseRowMenu()
	}

	const serviceColumns = useMemo<GridColDef<VehicleServiceTotalsRow>[]>(
		() => [
			{ field: 'requisicao', headerName: 'Requisição', width: 101 },
			{ field: 'motorista', headerName: 'Motorista', width: 120 },
			{ field: 'numMec', headerName: 'Nr Mec', width: 75 },
			{ field: 'dataInicio', headerName: 'Data Início', width: 100 },
			{ field: 'horaInicio', headerName: 'Hora Início', width: 99 },
			{ field: 'localInicio', headerName: 'Local Início', width: 120 },
			{ field: 'localTermino', headerName: 'Local Término', width: 120 },
			{ field: 'horaTermino', headerName: 'Hora Término', width: 117 },
			{ field: 'dataTermino', headerName: 'Data Término', width: 117 },
			{ field: 'cliente', headerName: 'Cliente', minWidth: 160, flex: 1 },
			{
				field: 'actions',
				headerName: '',
				width: 40,
				renderCell: params => (
					<Box
						className='serviceRowActions'
						sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
					>
						<IconButton
							size='small'
							onClick={event => handleOpenRowMenu(event, params.row.id)}
							sx={{ padding: 0.5 }}
						>
							<MoreVertRoundedIcon sx={{ fontSize: '18px', color: '#00A5FF' }} />
						</IconButton>
					</Box>
				),
			},
		],
		[handleOpenRowMenu],
	)

	const totalsColumns = useMemo<GridColDef<VehicleServiceTotalsByTypeRow>[]>(
		() => [
			{ field: 'tipo', headerName: 'Tipo', width: 140 },
			{ field: 'numero', headerName: 'Número Serviços', width: 138 },
			{ field: 'valor', headerName: 'Valor (€)', width: 100 },
			{ field: 'kmsCarga', headerName: 'Kms Carga', width: 100 },
			{ field: 'kmsVazio', headerName: 'Kms Vazio', width: 100 },
			{ field: 'totalKms', headerName: 'Total Kms', width: 100 },
		],
		[],
	)

	const handleClearFilters = () => {
		setStartDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setEndDate(dayjs('08/05/2024', 'DD/MM/YYYY'))
		setFilial('Transol')
		setVehicle('0046')
		setIncludeScheduleTime(true)
	}

	const handleOpenVehicleSheet = () => {
		const vehicleId = data?.vehicleId ?? 1
		closeModal()
		navigate(`/schedules/vehicle/${vehicleId}`)
	}

	const handlePrintTotals = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(services)
			return
		}
		console.log('Imprimir serviços por viatura', services)
	}

	const handleSendTotals = () => {
		if (typeof handleSend === 'function') {
			handleSend(services)
			return
		}
		console.log('Enviar serviços por viatura', services)
	}

	const handleExportTotals = () => {
		if (typeof handleExport === 'function') {
			handleExport(services)
			return
		}
		console.log('Exportar serviços por viatura', services)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box sx={{ padding: '16px 24px' }}>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Serviços e Totais por Viatura
					</Typography>
				</Box>

				<Box sx={{ padding: '0 24px 24px', display: 'grid', gap: '24px' }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Data Inicial'
								value={startDate}
								onChange={newValue => setStartDate(newValue)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
						</LocalizationProvider>
						<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>
							{String.fromCharCode(0x2014)}
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Data Final'
								value={endDate}
								onChange={newValue => setEndDate(newValue)}
								format='DD/MM/YYYY'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
							/>
						</LocalizationProvider>
						<TextField
							select
							label='Filial'
							value={filial}
							onChange={event => setFilial(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '160px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							<MenuItem value='Transol'>Transol</MenuItem>
						</TextField>
						<TextField
							select
							label='Viatura'
							value={vehicle}
							onChange={event => setVehicle(event.target.value)}
							size='small'
							sx={{ ...inputSx, minWidth: '160px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
						>
							<MenuItem value='0046'>0046</MenuItem>
						</TextField>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={includeScheduleTime}
									onChange={event => setIncludeScheduleTime(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label={<Typography sx={checkboxLabelSx}>Hora da Escala</Typography>}
							sx={{ margin: 0 }}
						/>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleClearFilters}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '12px',
								textTransform: 'none',
								marginLeft: 'auto',
								padding: '4px 0',
								'&:hover': { backgroundColor: 'transparent' },
							}}
						>
							Limpar Filtros
						</CustomButton>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							alignItems: 'center',
							gap: '16px',
						}}
					>
						<Typography
							sx={{
								fontSize: '24px',
								fontWeight: 600,
								lineHeight: 1.334,
								color: '#092056',
							}}
						>
							{services.length} Serviços
						</Typography>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '16px',
								marginLeft: 'auto',
								flexWrap: 'wrap',
								justifyContent: 'flex-end',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '16px',
									flexWrap: 'wrap',
									justifyContent: 'flex-end',
								}}
							>
								{[
									{ label: 'Kms Carga', value: stats.kmsCarga },
									{ label: 'Kms Vazio', value: stats.kmsVazio },
									{ label: 'Total Kms', value: stats.totalKms },
									{ label: 'Duração', value: stats.duracao },
								].map(stat => (
									<TextField
										key={stat.label}
										label={stat.label}
										value={stat.value}
										size='small'
										InputProps={{ readOnly: true }}
										InputLabelProps={{ shrink: true }}
										sx={{ ...readonlyFieldSx, width: '93px' }}
									/>
								))}
							</Box>
							<CustomButton
								size='small'
								onClick={handleOpenVehicleSheet}
								sx={{
									background: '#00A5FF',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '4px',
									padding: '4px 10px',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									minHeight: '30px',
								}}
							>
								Ficha da Viatura
							</CustomButton>
							<CustomButton
								size='small'
								sx={{
									background: '#00A5FF',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '4px',
									padding: '4px 10px',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									minHeight: '30px',
								}}
							>
								Docs Viatura
							</CustomButton>
							<ToggleButtonGroup
								value={servicesDensity}
								exclusive
								onChange={(_, value) => value && setServicesDensity(value)}
								size='small'
								sx={toggleGroupSx}
							>
								<ToggleButton
									value='compact'
									aria-label='densidade compacta'
								>
									<TableRowsIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: servicesDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
								<ToggleButton
									value='standard'
									aria-label='densidade espaçada'
								>
									<ViewHeadlineIcon
										sx={{
											width: '14.4px',
											height: '14.4px',
											color: servicesDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
										}}
									/>
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={services}
						columns={serviceColumns}
						density={servicesDensity}
						columnHeaderHeight={40}
						disableRowSelectionOnClick
						animateRows={animateRows}
						pagination={true}
						pageSize={10}
						getRowClassName={params => {
							if (params.indexRelativeToCurrentPage === 2) return 'row--warning'
							if (params.indexRelativeToCurrentPage === 3) return 'row--alert'
							return ''
						}}
						sx={[tableSx, servicesTableSx]}
					/>
					<Menu
						anchorEl={rowMenuAnchorEl}
						open={Boolean(rowMenuAnchorEl)}
						onClose={handleCloseRowMenu}
						PaperProps={{ sx: menuPaperSx }}
						MenuListProps={{ sx: { padding: '8px 0' } }}
					>
						<MenuItem
							onClick={() => handleRowMenuAction('Ficha do Motorista')}
							sx={{
								fontFamily: 'Poppins, sans-serif',
								fontSize: '12px',
								fontWeight: 500,
								lineHeight: 1.5,
								color: '#092056',
								padding: '6px 16px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<span>Ficha do Motorista</span>
							<ChevronRightRoundedIcon sx={{ fontSize: '18px', color: '#00A5FF' }} />
						</MenuItem>
						<MenuItem
							onClick={() => handleRowMenuAction('Serviços e Totais do Motorista')}
							sx={{
								fontFamily: 'Poppins, sans-serif',
								fontSize: '12px',
								fontWeight: 500,
								lineHeight: 1.5,
								color: '#092056',
								padding: '6px 16px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<span>Serviços e Totais do Motorista</span>
							<ChevronRightRoundedIcon sx={{ fontSize: '18px', color: '#00A5FF' }} />
						</MenuItem>
					</Menu>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: '16px',
							minHeight: '32px',
						}}
					>
						<Typography
							sx={{
								fontSize: '24px',
								fontWeight: 600,
								lineHeight: 1.334,
								color: '#092056',
							}}
						>
							Totais por Tipo de Serviço
						</Typography>
						<ToggleButtonGroup
							value={totalsDensity}
							exclusive
							onChange={(_, value) => value && setTotalsDensity(value)}
							size='small'
							sx={toggleGroupSx}
						>
							<ToggleButton
								value='compact'
								aria-label='densidade compacta'
							>
								<TableRowsIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: totalsDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
							<ToggleButton
								value='standard'
								aria-label='densidade espaçada'
							>
								<ViewHeadlineIcon
									sx={{
										width: '14.4px',
										height: '14.4px',
										color: totalsDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
									}}
								/>
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={totals}
						columns={totalsColumns}
						density={totalsDensity}
						columnHeaderHeight={40}
						disableRowSelectionOnClick
						animateRows={animateRows}
						pagination={true}
						pageSize={10}
						getRowClassName={params => (params.row?.tipo === 'Totais' ? 'row--totals' : '')}
						sx={[tableSx, totalsTableSx]}
					/>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '8px 24px 16px',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					{footerVariant === 'send' ? (
						<>
							<CustomButton
								variant='text'
								onClick={handlePrintTotals}
								sx={popupSecondaryButtonSx}
							>
								Imprimir
							</CustomButton>
							<CustomButton
								variant='contained'
								color='primary'
								onClick={handleSendTotals}
								sx={popupActionButtonSx}
							>
								Enviar
							</CustomButton>
						</>
					) : (
						<>
							<CustomButton
								variant='text'
								onClick={handleExportTotals}
								sx={popupSecondaryButtonSx}
							>
								Exportar
							</CustomButton>
							<CustomButton
								variant='contained'
								color='primary'
								onClick={handlePrintTotals}
								sx={popupActionButtonSx}
							>
								Imprimir
							</CustomButton>
						</>
					)}
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalVehicleServiceTotals
